/**
 * EditPageField - Consistent Form Field Layout
 *
 * A wrapper component for form fields to ensure consistent styling
 * across edit pages.
 */

import React from 'react';
import { cn } from '@/lib/utils';

interface IEditPageFieldProps {
	label?: string;
	required?: boolean;
	helpText?: string;
	error?: string;
	children: React.ReactNode;
	className?: string;
	labelClassName?: string;
	fieldClassName?: string;
	isHorizontal?: boolean;
}

export const EditPageField = ({
	label,
	required,
	helpText,
	error,
	children,
	className,
	labelClassName,
	fieldClassName,
	isHorizontal = false,
}: IEditPageFieldProps) => {
	if (isHorizontal) {
		return (
			<div className={cn('grid grid-cols-1 gap-2 sm:grid-cols-12 sm:gap-3', className)}>
				{label && (
					<label className={cn('sm:col-span-3 text-sm font-medium', labelClassName)}>
						{label}
						{required && <span className='text-destructive ml-1'>*</span>}
					</label>
				)}
				<div className={cn('sm:col-span-9 space-y-1', fieldClassName)}>
					{children}
					{helpText && !error && (
						<p className='text-xs text-muted-foreground'>{helpText}</p>
					)}
					{error && <p className='text-xs font-medium text-destructive'>{error}</p>}
				</div>
			</div>
		);
	}

	return (
		<div className={cn('space-y-2.5', className)}>
			{label && (
				<label className={cn('block text-sm font-medium', labelClassName)}>
					{label}
					{required && <span className='text-destructive ml-1'>*</span>}
				</label>
			)}
			<div className={fieldClassName}>
				{children}
			</div>
			{helpText && !error && (
				<p className='text-xs text-muted-foreground'>{helpText}</p>
			)}
			{error && <p className='text-xs font-medium text-destructive'>{error}</p>}
		</div>
	);
};

export default EditPageField;